"use client";
// Port Wingo UI (React 19 + Tailwind 4) → Tailwind 3.4, preluat din portul ERP-ului (React 18). Vezi components/wingo/README.md.

// Wingo UI Pro · https://wingo-ui.com/legal/license

import {
  createContext,
  useContext,
  useId,
  useMemo,
  type ComponentProps,
  type CSSProperties,
  type ReactNode,
} from "react";
import { AnimatePresence, motion } from "motion/react";
import { CopyButton } from "@/components/wingo/ui/copy-button";
import { Skeleton } from "@/components/wingo/ui/skeleton";
import { durations, easings, presets, useMotionPreset, type MotionPreset } from "@/components/wingo/lib/motion";
import { useDefaults } from "@/components/wingo/lib/ui-config";
import { cn } from "@/components/wingo/lib/utils";
import { withRef } from "@/components/wingo/lib/with-ref";

export type DescriptionListLayout = "auto" | "stacked" | "inline";
export type DescriptionListVariant = "plain" | "divided" | "surface" | "grouped";
export type DescriptionListSize = "sm" | "md" | "lg";
export type DescriptionListColumns = 1 | 2 | 3;
export type DescriptionListSpan = 1 | 2 | 3 | "full";
export type DescriptionListSlot =
  | "header"
  | "title"
  | "description"
  | "actions"
  | "body"
  | "grid"
  | "item"
  | "term"
  | "icon"
  | "details"
  | "value"
  | "hint"
  | "copy"
  | "action"
  | "skeleton";

export type DescriptionListItem = {
  // react key; falls back to the index
  id?: string;
  label: ReactNode;
  value: ReactNode;
  // a muted caption under the value
  description?: ReactNode;
  icon?: ReactNode;
  // true copies the value's text (string and number values only); a string copies that string; false opts out of copyable
  copy?: boolean | string;
  // columns the item takes at wide container sizes; clamps to the list's columns
  span?: DescriptionListSpan;
  // monospace value for ids, codes and ibans; long unbroken values break anywhere
  mono?: boolean;
  // trailing slot after the value: a link button, an inline edit trigger, a menu
  action?: ReactNode;
  hidden?: boolean;
};

// ---------- recipes ----------

const PANEL = "rounded-2xl bg-surface text-surface-foreground shadow-raised";

// the one grid cell the skeleton and the value share while they crossfade
const STACK = "grid min-w-0 grid-cols-[minmax(0,1fr)] [grid-template-areas:'stack'] *:[grid-area:stack]";

// label 12 / 13 / 14px, value 13 / 14 / 15px; the term shares the value's line height so an inline row lines up
const SIZES: Record<
  DescriptionListSize,
  {
    term: string;
    details: string;
    mono: string;
    row: string;
    gapY: string;
    gapX: string;
    stack: string;
    copy: string;
    bar: string;
    line: string;
    icon: string;
  }
> = {
  sm: {
    term: "text-xs/5",
    details: "text-[13px]/5",
    mono: "[font-family:ui-monospace,SFMono-Regular,Menlo,Consolas,monospace] text-[12px]/5",
    row: "py-2",
    gapY: "gap-y-4",
    gapX: "gap-x-5",
    stack: "gap-0.5",
    copy: "-my-1",
    bar: "h-3",
    line: "h-5",
    icon: "[&_svg:not([class*='size-'])]:size-3.5",
  },
  md: {
    term: "text-[13px]/5",
    details: "text-sm/5",
    mono: "[font-family:ui-monospace,SFMono-Regular,Menlo,Consolas,monospace] text-[12.5px]/5",
    row: "py-3",
    gapY: "gap-y-5",
    gapX: "gap-x-6",
    stack: "gap-1",
    copy: "-my-1",
    bar: "h-3.5",
    line: "h-5",
    icon: "[&_svg:not([class*='size-'])]:size-4",
  },
  lg: {
    term: "text-sm/6",
    details: "text-[15px]/6",
    mono: "[font-family:ui-monospace,SFMono-Regular,Menlo,Consolas,monospace] text-[13.5px]/6",
    row: "py-4",
    gapY: "gap-y-6",
    gapX: "gap-x-8",
    stack: "gap-1",
    copy: "-my-0.5",
    bar: "h-4",
    line: "h-6",
    icon: "[&_svg:not([class*='size-'])]:size-4",
  },
};

// ios inset rows: label left, value right, 44px rows even on phones; the header lines up with the row padding
const GROUPED_SIZES: Record<
  DescriptionListSize,
  { row: string; text: string; mono: string; line: string; header: string }
> = {
  sm: {
    row: "min-h-11 gap-x-3 px-3.5 py-2",
    text: "text-sm/5",
    mono: "[font-family:ui-monospace,SFMono-Regular,Menlo,Consolas,monospace] text-[12.5px]/5",
    line: "h-5",
    header: "px-3.5",
  },
  md: {
    row: "min-h-11 gap-x-4 px-4 py-2.5",
    text: "text-[15px]/5",
    mono: "[font-family:ui-monospace,SFMono-Regular,Menlo,Consolas,monospace] text-[13px]/5",
    line: "h-5",
    header: "px-4",
  },
  lg: {
    row: "min-h-13 gap-x-4 px-4 py-3",
    text: "text-base/6",
    mono: "[font-family:ui-monospace,SFMono-Regular,Menlo,Consolas,monospace] text-[14px]/6",
    line: "h-6",
    header: "px-4",
  },
};

// one column below 32rem of container width, two from 32rem, three from 48rem
const GRID_COLUMNS: Record<DescriptionListColumns, string> = {
  1: "grid-cols-1",
  2: "grid-cols-1 @min-[32rem]/description-list:grid-cols-2",
  3: "grid-cols-1 @min-[32rem]/description-list:grid-cols-2 @min-[48rem]/description-list:grid-cols-3",
};

const STACKED = "flex flex-col";
// label column + value column once the container has room; the label column never takes more than 35%
const INLINE =
  "flex flex-col @min-[24rem]/description-list:grid @min-[24rem]/description-list:grid-cols-[minmax(0,min(var(--label-width),35%))_minmax(0,1fr)] @min-[24rem]/description-list:items-baseline @min-[24rem]/description-list:gap-x-4";
const INLINE_WIDE =
  "flex flex-col @min-[40rem]/description-list:grid @min-[40rem]/description-list:grid-cols-[minmax(0,min(var(--label-width),35%))_minmax(0,1fr)] @min-[40rem]/description-list:items-baseline @min-[40rem]/description-list:gap-x-4";

// hidden until the row is hovered or holds focus with a mouse; always there on touch and while it shows the check
const COPY_REVEAL =
  "size-7 shrink-0 opacity-0 pointer-coarse:after:-inset-2 group-hover/item:opacity-100 group-focus-within/item:opacity-100 pointer-coarse:opacity-100 data-[state=copied]:opacity-100 data-[state=error]:opacity-100";
// grouped values sit flush right, so a hidden button would leave a ragged edge: it stays visible, muted like the value
const COPY_GROUPED =
  "size-7 shrink-0 pointer-coarse:after:-inset-2 [--tone-soft-foreground:rgb(var(--wui-muted-foreground))] hover:[--tone-soft-foreground:rgb(var(--wui-foreground))]";

// skeletons crossfade into values: quick and calm
const FADE: MotionPreset = {
  ...presets.fade,
  exit: {
    opacity: 0,
    transition: { duration: durations.fast, ease: easings.out },
  },
  transition: { duration: durations.fast, ease: easings.out },
};

// grouped rows put a value longer than this under the label on narrow containers
const LONG_VALUE = 24;

const LAYOUTS: DescriptionListLayout[] = ["auto", "stacked", "inline"];
const VARIANTS: DescriptionListVariant[] = ["plain", "divided", "surface", "grouped"];
const TITLE_TAGS = { h2: "h2", h3: "h3", h4: "h4" } as const;

function known<T extends string | number>(options: readonly T[], value: unknown, fallback: T): T {
  return options.includes(value as T) ? (value as T) : fallback;
}

const isEmpty = (value: ReactNode) => value === null || value === undefined || value === "" || value === false;

const textOf = (node: ReactNode) => (typeof node === "string" || typeof node === "number" ? String(node) : "");

// deterministic "random" widths so the server and the browser draw the same skeleton
const barWidth = (index: number, min: number, spread: number) => min + ((index * 37 + 11) % (spread + 1));

function hash(text: string) {
  let h = 0;
  for (let i = 0; i < text.length; i++) h = (h * 31 + text.charCodeAt(i)) | 0;
  return Math.abs(h);
}

function copyTextOf(copy: boolean | string | undefined, value: ReactNode, copyable: boolean): string | null {
  if (copy === false) return null;
  if (typeof copy === "string") return copy.trim() ? copy : null;
  if (!copy && !copyable) return null;
  // react elements are not copyable unless copy names the text
  const text = textOf(value).trim();
  return text || null;
}

function spanClass(span: DescriptionListSpan | undefined, columns: DescriptionListColumns) {
  if (columns === 1 || span === undefined || span === 1) return undefined;
  const wanted = span === "full" ? columns : Number(span);
  if (!Number.isFinite(wanted) || wanted <= 1) return undefined;
  if (wanted >= columns) return "col-span-full";
  // span 2 of 3: two of three columns at wide sizes, the full row once the grid drops to two
  return "@min-[32rem]/description-list:col-span-2";
}

// ---------- context ----------

type ListContext = {
  size: DescriptionListSize;
  layout: DescriptionListLayout;
  variant: DescriptionListVariant;
  columns: DescriptionListColumns;
  copyable: boolean;
  copyLabel: string;
  copiedLabel: string;
  emptyValue: ReactNode;
  loading: boolean;
  classNames?: Partial<Record<DescriptionListSlot, string>>;
};

const DEFAULT_CONTEXT: ListContext = {
  size: "md",
  layout: "auto",
  variant: "divided",
  columns: 2,
  copyable: false,
  copyLabel: "Copy",
  copiedLabel: "Copied",
  emptyValue: "–",
  loading: false,
};

const DescriptionListContext = createContext<ListContext>(DEFAULT_CONTEXT);

function itemLayout(ctx: ListContext) {
  if (ctx.variant === "grouped") return undefined;
  if (ctx.layout === "inline") return INLINE;
  if (ctx.layout === "auto" && ctx.columns === 1) return INLINE_WIDE;
  return STACKED;
}

// ---------- parts ----------

export type DescriptionTermProps = ComponentProps<"dt"> & {
  icon?: ReactNode;
};

function DescriptionTermRender({ icon, className, children, ...props }: DescriptionTermProps) {
  const ctx = useContext(DescriptionListContext);
  const s = SIZES[ctx.size];
  const grouped = ctx.variant === "grouped";
  return (
    <dt
      data-slot="description-term"
      className={cn(
        "flex min-w-0 items-start gap-1.5",
        // a long grouped label wraps at 60% instead of pushing the value out of the row
        grouped
          ? ["max-w-[60%] shrink-0 text-foreground", GROUPED_SIZES[ctx.size].text]
          : ["text-muted-foreground", s.term],
        ctx.classNames?.term,
        className,
      )}
      {...props}
    >
      {icon && (
        <span
          data-slot="description-term-icon"
          aria-hidden
          className={cn(
            "flex shrink-0 items-center text-muted-foreground",
            grouped ? [GROUPED_SIZES[ctx.size].line, "[&_svg:not([class*='size-'])]:size-4"] : [s.line, s.icon],
            ctx.classNames?.icon,
          )}
        >
          {icon}
        </span>
      )}
      {/* self-baseline makes the label text the dt's baseline, so an inline row lines up with a taller value */}
      <span data-slot="description-term-label" className="min-w-0 flex-1 self-baseline">
        {children}
      </span>
    </dt>
  );
}

export type DescriptionDetailsProps = ComponentProps<"dd">;

function DescriptionDetailsRender({ className, ...props }: DescriptionDetailsProps) {
  const ctx = useContext(DescriptionListContext);
  const grouped = ctx.variant === "grouped";
  return (
    <dd
      data-slot="description-details"
      className={cn(
        "min-w-0",
        // right-aligned grouped values are mostly amounts and dates: tabular figures line the digits up. not
        // elsewhere, inter's tnum also widens hyphens in prose ("Cluj-Napoca")
        grouped
          ? ["text-end text-muted-foreground tabular-nums", GROUPED_SIZES[ctx.size].text]
          : ["text-foreground", SIZES[ctx.size].details],
        ctx.classNames?.details,
        className,
      )}
      {...props}
    />
  );
}

export type DescriptionItemProps = Omit<ComponentProps<"div">, "children"> &
  Partial<Omit<DescriptionListItem, "id" | "label" | "value">> & {
    label?: ReactNode;
    value?: ReactNode;
    // the value; without a label, the item's own content (DescriptionTerm + DescriptionDetails)
    children?: ReactNode;
  };

function DescriptionItemRender({ label, value, children, ...props }: DescriptionItemProps) {
  if (props.hidden) return null;
  if (label === undefined) {
    return <ItemShell {...props}>{children}</ItemShell>;
  }
  const resolved = value !== undefined ? value : children;
  return <ItemView {...props} label={label} value={resolved} index={hash(textOf(label))} />;
}

type ShellProps = Omit<ComponentProps<"div">, "children"> & {
  span?: DescriptionListSpan;
  long?: boolean;
  children?: ReactNode;
};

// the <div> around a dt / dd pair (valid inside a <dl>) with the row look of the list
function ItemShell({ span, long = false, className, children, ...props }: ShellProps) {
  const ctx = useContext(DescriptionListContext);
  const grouped = ctx.variant === "grouped";
  const s = SIZES[ctx.size];
  return (
    <div
      data-slot="description-item"
      data-span={span && span !== 1 ? span : undefined}
      data-long={long || undefined}
      className={cn(
        "group/item relative min-w-0",
        grouped
          ? [
              "flex items-center justify-between",
              GROUPED_SIZES[ctx.size].row,
              // a long value moves under its label while the panel is narrow
              long &&
                "flex-col items-stretch justify-center gap-y-0.5 @min-[28rem]/description-list:flex-row @min-[28rem]/description-list:items-center @min-[28rem]/description-list:justify-between",
            ]
          : [itemLayout(ctx), s.stack, spanClass(span, ctx.columns)],
        ctx.variant === "divided" && ["border-t border-foreground/10", s.row],
        ctx.classNames?.item,
        className,
      )}
      {...props}
    >
      {children}
    </div>
  );
}

type ItemViewProps = Omit<DescriptionItemProps, "label" | "value" | "children"> & {
  label: ReactNode;
  value: ReactNode;
  index: number;
  // the label is a skeleton too (loading with no items)
  fakeLabel?: boolean;
};

function ItemView({
  label,
  value,
  description,
  icon,
  copy,
  span,
  mono = false,
  action,
  hidden,
  index,
  fakeLabel = false,
  ...props
}: ItemViewProps) {
  const ctx = useContext(DescriptionListContext);
  const fade = useMotionPreset(FADE);
  if (hidden) return null;

  const grouped = ctx.variant === "grouped";
  const s = SIZES[ctx.size];
  const empty = isEmpty(value);
  const copyText = empty ? null : copyTextOf(copy, value, ctx.copyable);
  const labelText = textOf(label).trim();
  const long = grouped && textOf(value).length > LONG_VALUE;

  return (
    <ItemShell span={span} long={long} {...props}>
      <DescriptionTerm icon={fakeLabel ? undefined : icon}>
        {fakeLabel ? (
          <span className={cn("flex w-full items-center", grouped ? GROUPED_SIZES[ctx.size].line : s.line)}>
            <Skeleton
              shape="line"
              width={grouped ? barWidth(index, 72, 48) : `${barWidth(index, 28, 24)}%`}
              className={cn(s.bar, "min-w-16", ctx.classNames?.skeleton)}
            />
          </span>
        ) : (
          label
        )}
      </DescriptionTerm>
      <DescriptionDetails className={cn(STACK, long && "text-start @min-[28rem]/description-list:text-end")}>
        <AnimatePresence initial={false}>
          {ctx.loading ? (
            <motion.div
              key="skeleton"
              {...fade}
              aria-hidden
              className={cn(
                "flex min-w-0 items-center",
                grouped ? [GROUPED_SIZES[ctx.size].line, "justify-end", long && "justify-start"] : s.line,
              )}
            >
              <Skeleton
                shape="line"
                width={grouped ? barWidth(index + 3, 64, 64) : `${barWidth(index, 40, 40)}%`}
                className={cn(s.bar, ctx.classNames?.skeleton)}
              />
            </motion.div>
          ) : (
            <motion.div key="value" {...fade} className="flex min-w-0 flex-col gap-0.5">
              <div
                data-slot="description-value-row"
                className={cn(
                  "flex min-w-0 items-start gap-1.5",
                  grouped && ["justify-end", long && "justify-start @min-[28rem]/description-list:justify-end"],
                )}
              >
                <span
                  data-slot="description-value"
                  data-empty={empty || undefined}
                  className={cn(
                    // long unbroken values (ibans, urls, emails) break inside a word only when it does not fit, spaces first
                    "min-w-0 [overflow-wrap:anywhere]",
                    // element values (an avatar + name, a badge) never outgrow the cell, so their own truncate works
                    "*:max-w-full",
                    // alone in its row the value may fill it (an inline edit, a select); next to a button it hugs its text
                    !copyText && !action && "flex-1",
                    mono && (grouped ? GROUPED_SIZES[ctx.size].mono : s.mono),
                    empty && "text-muted-foreground",
                    ctx.classNames?.value,
                  )}
                >
                  {empty ? ctx.emptyValue : value}
                </span>
                {copyText && (
                  <span data-slot="description-copy" className={cn("flex shrink-0", grouped ? "-my-1" : s.copy)}>
                    <CopyButton
                      value={copyText}
                      size="sm"
                      variant="ghost"
                      copyLabel={labelText ? `${ctx.copyLabel} ${labelText}` : ctx.copyLabel}
                      copiedLabel={ctx.copiedLabel}
                      className={cn(grouped ? COPY_GROUPED : COPY_REVEAL, ctx.classNames?.copy)}
                    />
                  </span>
                )}
                {action && (
                  <span
                    data-slot="description-action"
                    className={cn("ms-auto flex shrink-0 items-center", grouped && "ms-0", ctx.classNames?.action)}
                  >
                    {action}
                  </span>
                )}
              </div>
              {description && (
                <p data-slot="description-hint" className={cn("text-xs text-muted-foreground", ctx.classNames?.hint)}>
                  {description}
                </p>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </DescriptionDetails>
    </ItemShell>
  );
}

// ---------- DescriptionList ----------

export type DescriptionListProps = Omit<ComponentProps<"div">, "title" | "children"> & {
  items?: DescriptionListItem[];
  // DescriptionItem parts instead of items
  children?: ReactNode;
  // columns at container widths from 32rem (2) and 48rem (3); one column below
  columns?: DescriptionListColumns;
  // stacked: label above value. inline: label column + value column. auto: inline for one column on a wide container
  layout?: DescriptionListLayout;
  // px width of the label column in the inline layout (never more than 35% of the row)
  labelWidth?: number;
  // plain: no lines. divided: hairlines between rows. surface: a raised panel. grouped: ios inset rows
  variant?: DescriptionListVariant;
  // label 12 / 13 / 14px, value 13 / 14 / 15px
  size?: DescriptionListSize;
  // every item with a string or number value gets a copy button
  copyable?: boolean;
  // the copy button's label, followed by the item label: "Copy CUI"
  copyLabel?: string;
  copiedLabel?: string;
  // shown, muted, for null, undefined and "" values
  emptyValue?: ReactNode;
  // values become skeleton bars; with no items, skeletonCount fake rows
  loading?: boolean;
  skeletonCount?: number;
  title?: ReactNode;
  description?: ReactNode;
  // buttons at the end of the header
  actions?: ReactNode;
  titleAs?: "h2" | "h3" | "h4";
  classNames?: Partial<Record<DescriptionListSlot, string>>;
};

function DescriptionListRender(descriptionListProps: DescriptionListProps) {
  const {
    items,
    children,
    columns = 2,
    layout = "auto",
    labelWidth = 160,
    variant = "divided",
    size = "md",
    copyable = false,
    copyLabel = "Copy",
    copiedLabel = "Copied",
    emptyValue = "–",
    loading = false,
    skeletonCount = 6,
    title,
    description,
    actions,
    titleAs = "h3",
    classNames,
    className,
    style,
    ...props
  } = useDefaults("description-list", descriptionListProps);
  const titleId = useId();

  const safeSize = known<DescriptionListSize>(["sm", "md", "lg"], size, "md");
  const safeVariant = known(VARIANTS, variant, "divided");
  const safeLayout = known(LAYOUTS, layout, "auto");
  const safeColumns = known<DescriptionListColumns>([1, 2, 3], Number(columns), 2);
  const grouped = safeVariant === "grouped";
  const width = Number.isFinite(labelWidth) && labelWidth > 0 ? labelWidth : 160;
  const TitleTag = TITLE_TAGS[titleAs] ?? "h3";
  const s = SIZES[safeSize];

  const ctx = useMemo<ListContext>(
    () => ({
      size: safeSize,
      layout: safeLayout,
      variant: safeVariant,
      columns: grouped ? 1 : safeColumns,
      copyable,
      copyLabel,
      copiedLabel,
      emptyValue,
      loading,
      classNames,
    }),
    [
      safeSize,
      safeLayout,
      safeVariant,
      grouped,
      safeColumns,
      copyable,
      copyLabel,
      copiedLabel,
      emptyValue,
      loading,
      classNames,
    ],
  );

  const visible = items?.filter((item) => !item.hidden);
  const fakeRows = loading && !children && (!visible || visible.length === 0);
  const count = Math.min(Math.max(Math.round(Number(skeletonCount) || 0), 1), 24);

  const resolvedLayout = grouped
    ? "grouped"
    : safeLayout === "auto"
      ? safeColumns === 1
        ? "auto-inline"
        : "stacked"
      : safeLayout;

  const hasHeader = Boolean(title || description || actions);
  const Root = title ? "section" : "div";

  return (
    <DescriptionListContext.Provider value={ctx}>
      <Root
        data-slot="description-list"
        data-variant={safeVariant}
        data-size={safeSize}
        aria-labelledby={title ? titleId : undefined}
        className={cn(
          "flex w-full min-w-0 flex-col",
          safeVariant === "surface"
            ? [PANEL, "gap-4 p-4 sm:p-5"]
            : grouped
              ? "gap-2"
              : safeVariant === "divided"
                ? "gap-1"
                : "gap-4",
          className,
        )}
        style={{ "--label-width": `${width}px`, ...style } as CSSProperties}
        {...props}
      >
        {hasHeader && (
          <div
            data-slot="description-list-header"
            className={cn(
              "flex flex-wrap items-start justify-between gap-x-4 gap-y-2",
              grouped && GROUPED_SIZES[safeSize].header,
              classNames?.header,
            )}
          >
            {(title || description) && (
              <div data-slot="description-list-heading" className="flex min-w-0 flex-1 basis-48 flex-col gap-1">
                {title && (
                  <TitleTag
                    id={titleId}
                    data-slot="description-list-title"
                    className={cn("text-[15px] font-semibold tracking-tight text-foreground", classNames?.title)}
                  >
                    {title}
                  </TitleTag>
                )}
                {description && (
                  <p
                    data-slot="description-list-description"
                    className={cn("text-sm text-muted-foreground", classNames?.description)}
                  >
                    {description}
                  </p>
                )}
              </div>
            )}
            {actions && (
              <div
                data-slot="description-list-actions"
                className={cn("flex shrink-0 items-center gap-2", classNames?.actions)}
              >
                {actions}
              </div>
            )}
          </div>
        )}
        {/* the container the grid queries; divided rows draw a top hairline and the first row's is clipped away */}
        <div
          data-slot="description-list-body"
          className={cn(
            "@container/description-list min-w-0",
            safeVariant === "divided" && "overflow-y-clip",
            classNames?.body,
          )}
        >
          <dl
            data-slot="description-list-grid"
            data-layout={resolvedLayout}
            data-columns={grouped ? 1 : safeColumns}
            aria-busy={loading || undefined}
            className={cn(
              grouped
                ? [PANEL, "flex flex-col divide-y divide-foreground/10"]
                : ["grid", GRID_COLUMNS[safeColumns], s.gapX, safeVariant === "divided" ? "-mt-px" : s.gapY],
              classNames?.grid,
            )}
          >
            {fakeRows
              ? Array.from({ length: count }, (_, i) => <ItemView key={i} label="" value="" index={i} fakeLabel />)
              : (children ??
                visible?.map((item, i) => {
                  const { id, ...rest } = item;
                  return <ItemView key={id ?? i} {...rest} index={i} />;
                }))}
          </dl>
        </div>
      </Root>
    </DescriptionListContext.Provider>
  );
}

// React 18: ref-urile ajung la randare doar prin forwardRef (vezi lib/with-ref.ts)
export const DescriptionTerm = withRef(DescriptionTermRender, "DescriptionTerm");
export const DescriptionDetails = withRef(DescriptionDetailsRender, "DescriptionDetails");
export const DescriptionItem = withRef(DescriptionItemRender, "DescriptionItem");
export const DescriptionList = withRef(DescriptionListRender, "DescriptionList");
